import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Captions, CaptionsOff, Settings2, X, Loader2, ListVideo, Search, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SUPPORTED_LANGUAGES = [
  "English", "Spanish", "French", "Portuguese", "Japanese", "Korean",
  "Chinese", "Hindi", "Arabic", "German", "Italian", "Turkish", "Russian", "Filipino",
];

const LANG_BADGE = {
  English: "EN", Spanish: "ES", French: "FR", Portuguese: "PT", Japanese: "JA",
  Korean: "KO", Chinese: "ZH", Hindi: "HI", Arabic: "AR", German: "DE",
  Italian: "IT", Turkish: "TR", Russian: "RU", Filipino: "FIL",
};

const FONT_CLASSES = {
  small: "text-sm md:text-base",
  medium: "text-base md:text-2xl",
  large: "text-lg md:text-4xl",
};

const captionCache = {}; // key `${videoId}|${lang}` -> {available, cues}

export function clearCaptionCache(videoId) {
  Object.keys(captionCache).forEach((k) => {
    if (k.startsWith(`${videoId}|`)) delete captionCache[k];
  });
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export default function SubtitleOverlay({ videoId, playerRef, viewerLanguage, isDriver, onUploadCaptions, onSeek, refreshSignal }) {
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState("auto"); // "auto" or a language name
  const [fontSize, setFontSize] = useState("medium");
  const [position, setPosition] = useState("bottom");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ready | none | error
  const [cues, setCues] = useState([]);
  const [current, setCurrent] = useState("");
  const [activeStart, setActiveStart] = useState(null);
  const reqRef = useRef(0);
  const [msgMounted, setMsgMounted] = useState(false);
  const [msgVisible, setMsgVisible] = useState(false);
  const unavailableShownRef = useRef(new Set());

  const effectiveLang = useMemo(() => {
    if (mode !== "auto") return mode;
    return SUPPORTED_LANGUAGES.includes(viewerLanguage) ? viewerLanguage : "English";
  }, [mode, viewerLanguage]);

  // precompute cue end times
  const timedCues = useMemo(() => {
    return cues.map((c, i) => {
      const end = c.dur > 0 ? c.start + c.dur : (cues[i + 1] ? cues[i + 1].start : c.start + 4);
      return { start: c.start, end, text: c.text };
    });
  }, [cues]);

  // fetch captions when needed
  useEffect(() => {
    if (!enabled || !videoId) return;
    const key = `${videoId}|${effectiveLang}`;
    const cached = captionCache[key];
    if (cached) {
      if (cached.available) { setCues(cached.cues); setStatus("ready"); }
      else { setCues([]); setStatus("none"); }
      return;
    }
    const myReq = ++reqRef.current;
    setStatus("loading");
    setCues([]);
    setCurrent("");
    fetch(`${BACKEND_URL}/api/captions/${videoId}?lang=${encodeURIComponent(effectiveLang)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (myReq !== reqRef.current) return;
        if (d && d.available) {
          captionCache[key] = { available: true, cues: d.cues || [] };
          setCues(d.cues || []);
          setStatus("ready");
        } else {
          captionCache[key] = { available: false, cues: [] };
          setStatus("none");
        }
      })
      .catch(() => { if (myReq === reqRef.current) setStatus("error"); });
  }, [enabled, videoId, effectiveLang, refreshSignal]);

  // poll playback time -> active cue
  useEffect(() => {
    if (!enabled || status !== "ready" || timedCues.length === 0) { setCurrent(""); setActiveStart(null); return; }
    const id = setInterval(() => {
      const t = playerRef.current?.getTime?.();
      if (typeof t !== "number") return;
      const active = timedCues.find((c) => t >= c.start - 0.15 && t <= c.end + 0.25);
      setCurrent(active ? active.text : "");
      setActiveStart(active ? active.start : null);
    }, 250);
    return () => clearInterval(id);
  }, [enabled, status, timedCues, playerRef]);

  // "Captions unavailable" shows for 3s then fades — once per video, never again
  useEffect(() => {
    if (status !== "none") { setMsgMounted(false); setMsgVisible(false); return; }
    if (!videoId || unavailableShownRef.current.has(videoId)) return;
    unavailableShownRef.current.add(videoId);
    setMsgMounted(true);
    const show = requestAnimationFrame(() => setMsgVisible(true));
    const hide = setTimeout(() => setMsgVisible(false), 3000);
    const unmount = setTimeout(() => setMsgMounted(false), 3600);
    return () => { cancelAnimationFrame(show); clearTimeout(hide); clearTimeout(unmount); };
  }, [status, videoId]);

  const badge = LANG_BADGE[effectiveLang] || effectiveLang.slice(0, 2).toUpperCase();
  const autoLabel = `Auto (${SUPPORTED_LANGUAGES.includes(viewerLanguage) ? viewerLanguage : "English"})`;

  const filteredCues = query
    ? timedCues.filter((c) => c.text.toLowerCase().includes(query.toLowerCase()))
    : timedCues;

  const handleSeek = (start) => {
    if (!isDriver) { toast.info("Only the driver can seek the video"); return; }
    onSeek && onSeek(start);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const text = await f.text();
    onUploadCaptions && onUploadCaptions(text, f.name.toLowerCase().endsWith(".vtt") ? "vtt" : "srt", videoId);
    toast.success("Uploading captions…");
  };

  return (
    <>
      {/* Subtitle band — always at the BOTTOM of the video on mobile */}
      {enabled && status === "ready" && current && (
        <div
          data-testid="subtitle-overlay"
          className={`absolute z-20 left-1/2 -translate-x-1/2 w-[90%] max-w-[90%] px-2 text-center pointer-events-none ${
            position === "top" ? "top-[10%] max-md:top-auto max-md:bottom-[10%]" : "bottom-[10%]"
          }`}
        >
          <span
            data-testid="subtitle-text"
            className={`inline-block max-w-[90%] text-center text-white font-semibold leading-snug rounded px-3 py-1 ${FONT_CLASSES[fontSize]}`}
            style={{ background: "rgba(0,0,0,0.75)", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}
          >
            {current}
          </span>
        </div>
      )}

      {/* Language badge */}
      {enabled && status === "ready" && (
        <span
          data-testid="subtitle-badge"
          className={`absolute right-3 z-20 flex items-center gap-1 text-[11px] font-bold text-white bg-black/60 rounded-full px-2 py-0.5 pointer-events-none ${position === "top" ? "bottom-3" : "top-14"}`}
        >
          🌐 {badge}
        </span>
      )}

      {/* CC button + settings */}
      <div className="absolute top-3 max-md:top-14 right-3 z-30 flex flex-col items-end gap-2">
        <button
          data-testid="cc-button"
          onClick={() => { if (status === "none") return; setEnabled((v) => !v); }}
          title={status === "none" ? "No captions for this video" : enabled ? "Turn subtitles off" : "Turn subtitles on"}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${
            status === "none"
              ? "bg-black/50 border-white/10 text-[#71717A] cursor-not-allowed"
              : enabled
                ? "bg-[#00D9FF] border-[#00D9FF] text-white"
                : "bg-black/60 border-white/15 text-[#E4E4E7] hover:border-[#00D9FF]"
          }`}
        >
          {status === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (status === "none" || !enabled) ? <CaptionsOff className="w-3.5 h-3.5" /> : <Captions className="w-3.5 h-3.5" />}
          CC
        </button>

        {status === "none" && msgMounted && (
          <span
            data-testid="subtitle-unavailable"
            className={`text-[11px] text-white bg-black/70 rounded-md px-2 py-1.5 max-w-[240px] text-right leading-snug transition-opacity duration-500 ${msgVisible ? "opacity-100" : "opacity-0"}`}
          >
            Captions unavailable — this video has none, or YouTube blocked the request.
          </span>
        )}

        {enabled && status !== "none" && (
          <button
            data-testid="subtitle-settings-button"
            onClick={() => setSettingsOpen((v) => !v)}
            title="Subtitle settings"
            className="w-9 h-9 rounded-full bg-black/60 border border-white/15 text-white flex items-center justify-center hover:border-[#00D9FF] transition-colors"
          >
            <Settings2 className="w-4 h-4" />
          </button>
        )}

        {status === "ready" && (
          <button
            data-testid="transcript-toggle"
            onClick={() => setTranscriptOpen((v) => !v)}
            title="Transcript"
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${transcriptOpen ? "bg-[#00D9FF] border-[#00D9FF] text-white" : "bg-black/60 border-white/15 text-white hover:border-[#00D9FF]"}`}
          >
            <ListVideo className="w-4 h-4" />
          </button>
        )}

        {settingsOpen && enabled && (
          <div data-testid="subtitle-settings" className="w-60 bg-[#0E0E16] border border-white/10 rounded-2xl p-3 shadow-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white uppercase tracking-wide">Subtitles</span>
              <button data-testid="subtitle-settings-close" onClick={() => setSettingsOpen(false)} className="text-[#A0A0AB] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Language</label>
              <select
                data-testid="subtitle-lang-select"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="w-full h-9 bg-[#141c40] border border-white/10 rounded-lg px-2 text-sm text-white outline-none focus:border-[#00D9FF]"
              >
                <option value="auto">{autoLabel}</option>
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Font size</label>
              <div className="flex gap-1">
                {["small", "medium", "large"].map((s) => (
                  <button
                    key={s}
                    data-testid={`subtitle-fontsize-${s}`}
                    onClick={() => setFontSize(s)}
                    className={`flex-1 h-8 rounded-lg text-xs capitalize transition-colors ${fontSize === s ? "bg-[#00D9FF] text-navy-ink" : "bg-[#141c40] text-[#A0A0AB] hover:bg-white/10"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Position</label>
              <div className="flex gap-1">
                {["bottom", "top"].map((p) => (
                  <button
                    key={p}
                    data-testid={`subtitle-position-${p}`}
                    onClick={() => setPosition(p)}
                    className={`flex-1 h-8 rounded-lg text-xs capitalize transition-colors ${position === p ? "bg-[#00D9FF] text-navy-ink" : "bg-[#141c40] text-[#A0A0AB] hover:bg-white/10"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {isDriver && (
              <div className="space-y-1 border-t border-white/10 pt-3">
                <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Upload captions (.srt / .vtt)</label>
                <label
                  data-testid="upload-captions-label"
                  className="flex items-center justify-center gap-2 h-9 rounded-lg bg-[#141c40] border border-dashed border-white/20 text-xs text-[#A0A0AB] cursor-pointer hover:border-[#00D9FF] hover:text-white transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" /> Choose file
                  <input
                    data-testid="upload-captions-input"
                    type="file"
                    accept=".srt,.vtt"
                    onChange={handleFile}
                    className="hidden"
                  />
                </label>
                <p className="text-[10px] text-[#71717A]">Shared with the whole room, translated per viewer.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transcript panel */}
      {transcriptOpen && status === "ready" && (
        <div
          data-testid="transcript-panel"
          className="absolute top-14 left-3 bottom-4 z-30 w-[320px] max-w-[70%] flex flex-col bg-[#0A0A12]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-xs font-bold text-white uppercase tracking-wide">Transcript</span>
            <button data-testid="transcript-close" onClick={() => setTranscriptOpen(false)} className="text-[#A0A0AB] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-3 py-2 border-b border-white/10">
            <div className="flex items-center gap-2 h-9 bg-[#141c40] border border-white/10 rounded-lg px-2">
              <Search className="w-3.5 h-3.5 text-[#71717A]" />
              <input
                data-testid="transcript-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search subtitles…"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#71717A]"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto chat-scroll p-2 space-y-0.5" data-testid="transcript-list">
            {filteredCues.length === 0 && (
              <p className="text-xs text-[#71717A] text-center py-6">No matching lines</p>
            )}
            {filteredCues.map((c, i) => (
              <button
                key={`${c.start}-${i}`}
                data-testid="transcript-line"
                onClick={() => handleSeek(c.start)}
                className={`w-full text-left flex gap-2 rounded-lg px-2 py-1.5 transition-colors ${activeStart === c.start ? "bg-[#00D9FF]/25" : "hover:bg-white/5"} ${isDriver ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className="text-[11px] font-mono text-[#00D9FF] shrink-0 pt-0.5 w-10">{fmtTime(c.start)}</span>
                <span className="text-xs text-[#E4E4E7] leading-snug">{c.text}</span>
              </button>
            ))}
          </div>
          {!isDriver && (
            <p className="text-[10px] text-[#71717A] px-3 py-2 border-t border-white/10">Only the driver can jump the video.</p>
          )}
        </div>
      )}
    </>
  );
}
